import type { LinkType, TicketLink } from '@servicetim/shared';
import {
  portalPath,
  useApiContext,
  useDeleteLink,
  usePortalLinkTypes,
  useTicketLinks,
} from '@servicetim/ui/api';
import {
  AsyncPicker,
  Button,
  describe,
  Label,
  ProblemBanner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useTicketOptionLoader,
} from '@servicetim/ui/components';
import { useMutation } from '@tanstack/react-query';
import { Link2, LoaderCircle, Plus, X } from 'lucide-react';
import { useId, useState } from 'react';
import { RailCard } from './rail-card';
import { useTicketContext } from './ticket-context';
import { TicketKeyLink } from './ticket-link';

/** `<linkTypeId>:outward` (this ticket is the source) or `<linkTypeId>:inward` (the other one is). */
type DirectionChoice = `${string}:outward` | `${string}:inward`;

/**
 * `POST /tickets/{source}/links`: the source is this ticket for an outward label and the other
 * ticket for an inward one ("is blocked by X" = X blocks this). Local because the shared
 * `useCreateLink(ticketId)` is bound to one source.
 */
function useCreateDirectedLink(thisTicketId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({
      sourceId,
      targetId,
      linkTypeId,
    }: {
      sourceId: string;
      targetId: string;
      linkTypeId: string;
    }) =>
      request<TicketLink>('POST', portalPath(`/tickets/${sourceId}/links`), {
        body: { linkTypeId, targetTicketId: targetId },
      }),
    onSuccess: (_link, { sourceId, targetId }) =>
      invalidate(
        keys.ticket(thisTicketId).links,
        keys.ticket(thisTicketId).history,
        keys.ticket(sourceId).links,
        keys.ticket(targetId).links,
      ),
  });
}

function directionOptions(types: readonly LinkType[]): { value: DirectionChoice; label: string }[] {
  const out: { value: DirectionChoice; label: string }[] = [];
  for (const t of types) {
    if (t.name === 'clones') continue; // written by the clone endpoint only
    out.push({ value: `${t.id}:outward`, label: t.outward });
    if (t.inward !== t.outward) out.push({ value: `${t.id}:inward`, label: t.inward });
  }
  return out;
}

/** Typed links to other tickets (S-05-10): add with a type + direction, remove. */
export function LinksCard() {
  const { ticket } = useTicketContext();
  const links = useTicketLinks(ticket.id);
  const types = usePortalLinkTypes();
  const create = useCreateDirectedLink(ticket.id);
  const remove = useDeleteLink(ticket.id);
  const loadTickets = useTicketOptionLoader();
  const [adding, setAdding] = useState(false);
  const [choice, setChoice] = useState<DirectionChoice | ''>('');
  const [otherId, setOtherId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const typeId = useId();
  const options = directionOptions(types.data ?? []);

  const submit = async () => {
    if (!choice || !otherId) return;
    const [linkTypeId, direction] = choice.split(':') as [string, 'outward' | 'inward'];
    setError(null);
    try {
      await create.mutateAsync({
        linkTypeId,
        sourceId: direction === 'outward' ? ticket.id : otherId,
        targetId: direction === 'outward' ? otherId : ticket.id,
      });
      toast.success('Link added');
      setAdding(false);
      setChoice('');
      setOtherId(undefined);
    } catch (e) {
      setError(e);
    }
  };

  const items = links.data ?? [];
  return (
    <RailCard
      title={`Links${links.data ? ` (${items.length})` : ''}`}
      actions={
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setAdding((v) => !v)}
          aria-expanded={adding}
        >
          <Plus />
          Link
        </Button>
      }
    >
      {links.error ? <ProblemBanner error={links.error} size="sm" /> : null}
      {adding ? (
        <div className="space-y-2 rounded-md border border-border bg-surface-sunken p-3">
          <div className="space-y-1">
            <Label htmlFor={typeId}>This ticket…</Label>
            <Select value={choice} onValueChange={(v) => setChoice(v as DirectionChoice)}>
              <SelectTrigger id={typeId} size="sm">
                <SelectValue placeholder={types.isPending ? 'Loading…' : 'Relationship'} />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AsyncPicker
            value={otherId}
            onChange={setOtherId}
            load={loadTickets}
            placeholder="Find a ticket…"
            searchPlaceholder="Key (IT-42) or summary…"
          />
          {error ? <ProblemBanner error={error} size="sm" /> : null}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void submit()}
              disabled={!choice || !otherId || create.isPending}
            >
              {create.isPending ? <LoaderCircle className="animate-spin" /> : <Link2 />}
              Add link
            </Button>
          </div>
        </div>
      ) : null}
      {links.isSuccess && items.length === 0 ? <p className="text-fg-muted">No links.</p> : null}
      <ul className="space-y-2">
        {items.map((link) => (
          <li key={link.id} className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <span className="text-fg-muted text-xs">{link.label}</span>
              <div className="flex min-w-0 items-baseline gap-1.5">
                <TicketKeyLink ticketKey={link.ticket.key} />
                <span className="truncate text-fg-muted" title={link.ticket.summary}>
                  {link.ticket.summary}
                </span>
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="size-7 shrink-0"
              aria-label={`Remove link to ${link.ticket.key}`}
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(
                  { linkId: link.id, otherTicketId: link.ticket.id },
                  {
                    onSuccess: () => toast.success('Link removed'),
                    onError: (e) => toast.error(describe(e).title),
                  },
                )
              }
            >
              <X />
            </Button>
          </li>
        ))}
      </ul>
    </RailCard>
  );
}
